/**
 * RFC-091: Create New Client Form
 * Form to create a new client organization
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { trpc } from '@/lib/trpc';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@deepguide-ai/ui/card';
import { Input } from '@deepguide-ai/ui/input';
import { Label } from '@deepguide-ai/ui/label';
import { Button } from '@deepguide-ai/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select';
import { Alert, AlertDescription } from '@deepguide-ai/ui/alert';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { APP_NAME } from '@/lib/env';
export const Route = createFileRoute('/(protected)/partner/clients/new')({
  component: NewClientPage,
});

function NewClientPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [clientName, setClientName] = useState('');
  const [clientSlug, setClientSlug] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [allocatedWorkspaces, setAllocatedWorkspaces] = useState<number | undefined>(3);
  const [billingMode, setBillingMode] = useState<'partner' | 'client' | 'hybrid'>('partner');
  const [error, setError] = useState('');

  const createClientMutation = trpc.partner.createClient.mutationOptions();
  const createClient = useMutation({
    ...createClientMutation,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['partner'] });
      navigate({ to: '/partner/clients/$clientId', params: { clientId: data.clientOrg.id } });
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to create client');
    },
  });

  // Auto-generate slug from name
  const handleNameChange = (name: string) => {
    setClientName(name);
    if (!clientSlug || clientSlug === slugify(clientName)) {
      setClientSlug(slugify(name));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!clientName || !clientSlug || !ownerEmail) {
      setError('Please fill in all required fields');
      return;
    }

    if (!/^[a-z0-9-]+$/.test(clientSlug)) {
      setError('Slug can only contain lowercase letters, numbers, and hyphens');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    createClient.mutate({
      clientName,
      clientSlug,
      ownerEmail,
      ownerName: ownerName || undefined,
      allocatedWorkspaces,
      billingMode,
    });
  };

  return (
    <div className="p-8 max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/partner/clients">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Add New Client</h1>
          <p className="text-muted-foreground">
            Create a new client organization
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Client Information</CardTitle>
          <CardDescription>
            Basic details about the client organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="clientName">
                Organization Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="clientName"
                placeholder="Acme Corporation"
                value={clientName}
                onChange={(e) => handleNameChange(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="clientSlug">
                Organization Slug <span className="text-destructive">*</span>
              </Label>
              <Input
                id="clientSlug"
                placeholder="acme-corporation"
                value={clientSlug}
                onChange={(e) => setClientSlug(e.target.value)}
                pattern="[a-z0-9-]+"
                required
              />
              <p className="text-sm text-muted-foreground">
                Used in URLs: {clientSlug || 'your-slug'}.{APP_NAME.toLowerCase()}.ai
              </p>
            </div>

            <div className="border-t pt-6">
              <h3 className="font-medium mb-4">Owner Information</h3>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ownerEmail">
                    Owner Email <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="ownerEmail"
                    type="email"
                    placeholder="john@acme.com"
                    value={ownerEmail}
                    onChange={(e) => setOwnerEmail(e.target.value)}
                    required
                  />
                  <p className="text-sm text-muted-foreground">
                    Will receive invitation to set up the organization
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ownerName">Owner Name (Optional)</Label>
                  <Input
                    id="ownerName"
                    placeholder="John Doe"
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="font-medium mb-4">Resource Limits</h3>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="allocatedWorkspaces">Allocated Workspaces</Label>
                  <Input
                    id="allocatedWorkspaces"
                    type="number"
                    min="0"
                    placeholder="3"
                    value={allocatedWorkspaces !== undefined ? allocatedWorkspaces : ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setAllocatedWorkspaces(val ? parseInt(val, 10) : undefined);
                    }}
                  />
                  <p className="text-sm text-muted-foreground">
                    Leave empty for unlimited workspaces
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="billingMode">Billing Mode</Label>
                  <Select value={billingMode} onValueChange={(val: any) => setBillingMode(val)}>
                    <SelectTrigger id="billingMode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="partner">Partner Pays (Consolidated)</SelectItem>
                      <SelectItem value="client">Client Pays Directly</SelectItem>
                      <SelectItem value="hybrid">Hybrid (Shared)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    Who is responsible for billing
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={createClient.isPending}
                className="flex-1"
              >
                {createClient.isPending ? 'Creating...' : 'Create Client'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate({ to: '/partner/clients' })}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
